// ─── TERMS OF SERVICE ─────────────────────────────────────────────────────────

const LAST_UPDATED = 'April 23, 2026';

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 40 }}>
    <h2 style={{
      fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)',
      marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--border)',
    }}>
      {title}
    </h2>
    <div style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.75 }}>
      {children}
    </div>
  </section>
);

const P = ({ children, style }) => (
  <p style={{ marginBottom: 12, ...style }}>{children}</p>
);

const UL = ({ items }) => (
  <ul style={{ paddingLeft: 24, marginBottom: 12 }}>
    {items.map((item, i) => <li key={i} style={{ marginBottom: 6 }}>{item}</li>)}
  </ul>
);

const TermsOfService = () => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '60px 24px 80px' }}>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <a
          href="/"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 32 }}
        >
          <span style={{ fontSize: 32 }} aria-hidden="true">⏱</span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: 'var(--accent)' }}>Chronos</span>
        </a>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: 'var(--text)', marginBottom: 10 }}>
          Terms of Service
        </h1>
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>Last updated: {LAST_UPDATED}</p>
      </div>

      {/* Intro */}
      <P style={{ color: 'var(--text2)', marginBottom: 48, lineHeight: 1.8 }}>
        Welcome to Chronos. By creating an account or using our service, you agree to be bound by these Terms of Service.
        Please read them carefully. If you do not agree, do not use Chronos.
      </P>

      <Section title="1. Acceptance of Terms">
        <P>
          By accessing or using Chronos ("the Service"), you confirm that you are at least 13 years of age and have
          read, understood, and agreed to these Terms. If you are using the Service on behalf of an organisation,
          you represent that you have authority to bind that organisation to these Terms.
        </P>
        <P>
          We may update these Terms from time to time. Continued use of the Service after changes are posted
          constitutes acceptance of the revised Terms. We will notify you of material changes via email or a
          prominent notice in the app.
        </P>
      </Section>

      <Section title="2. Your Account">
        <P>You are responsible for maintaining the confidentiality of your password and for all activity that occurs under your account. You agree to:</P>
        <UL items={[
          'Provide accurate and complete registration information.',
          'Keep your password secure and not share it with others.',
          'Notify us immediately of any unauthorised use of your account.',
          'Not create more than one account per person without our express permission.',
        ]} />
        <P>
          We reserve the right to suspend or terminate accounts that violate these Terms or our Community Guidelines.
        </P>
      </Section>

      <Section title="3. Acceptable Use">
        <P>You may use Chronos only for lawful purposes and in accordance with these Terms. You agree not to:</P>
        <UL items={[
          'Post content that is illegal, abusive, harassing, defamatory, or otherwise objectionable.',
          'Impersonate any person or entity or misrepresent your affiliation.',
          'Upload content that infringes any patent, trademark, trade secret, copyright, or other proprietary right.',
          'Transmit any unsolicited or unauthorised advertising or promotional material (spam).',
          'Attempt to gain unauthorised access to any part of the Service or its related systems.',
          'Use automated means to access the Service without our prior written consent.',
          'Interfere with or disrupt the integrity or performance of the Service.',
        ]} />
      </Section>

      <Section title="4. User Content">
        <P>
          You retain ownership of content you create on Chronos (timelines, events, images, and associated text).
          By posting content, you grant Chronos a worldwide, non-exclusive, royalty-free licence to host, store,
          display, and distribute your content solely for the purpose of operating and improving the Service.
        </P>
        <P>
          You represent that you have all necessary rights to the content you post and that it does not violate
          the rights of any third party.
        </P>
        <P>
          We do not endorse any user content and are not responsible for content posted by users. We reserve the
          right (but not the obligation) to remove content that violates these Terms or our Community Guidelines.
        </P>
      </Section>

      <Section title="5. Privacy">
        <P>
          Your use of the Service is also governed by our{' '}
          <a href="/privacy" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Privacy Policy</a>,
          which is incorporated into these Terms by reference. By using Chronos, you consent to the data practices
          described in the Privacy Policy.
        </P>
      </Section>

      <Section title="6. Intellectual Property">
        <P>
          The Service and its original content (excluding user-submitted content), features, and functionality are
          and will remain the exclusive property of Chronos and its licensors. Our trademarks and trade dress may
          not be used in connection with any product or service without our prior written consent.
        </P>
        <P>
          We respect intellectual property rights and ask our users to do the same. If you believe that content on
          the Service infringes your copyright, please contact us with a description of the allegedly infringing
          material and we will investigate.
        </P>
      </Section>

      <Section title="7. Termination">
        <P>
          We may suspend or terminate your access to the Service at any time, with or without notice, for conduct
          that we believe violates these Terms or is harmful to other users, us, third parties, or the Service.
        </P>
        <P>
          You may close your account at any time from your account settings. Upon termination, your right to use
          the Service will immediately cease. Provisions that by their nature should survive termination (including
          ownership provisions, warranty disclaimers, and limitations of liability) shall survive.
        </P>
      </Section>

      <Section title="8. Disclaimers">
        <P>
          The Service is provided on an "as is" and "as available" basis without warranties of any kind, either
          express or implied, including but not limited to warranties of merchantability, fitness for a particular
          purpose, or non-infringement.
        </P>
        <P>
          We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful
          components. You use the Service at your own risk.
        </P>
      </Section>

      <Section title="9. Limitation of Liability">
        <P>
          To the fullest extent permitted by applicable law, Chronos and its operators shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of
          profits, data, goodwill, or other intangible losses, resulting from your access to or use of (or inability
          to access or use) the Service.
        </P>
        <P>
          In no event shall our total liability to you exceed the greater of (a) the amount you paid us in the
          12 months prior to the claim, or (b) one hundred US dollars ($100).
        </P>
      </Section>

      <Section title="10. Contact Us">
        <P>
          If you have any questions about these Terms of Service, please contact us at{' '}
          <a href="mailto:legal@chronos.app" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
            legal@chronos.app
          </a>.
        </P>
      </Section>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: 'var(--text3)' }}>
          <a href="/privacy" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Privacy Policy</a>
          {' · '}
          <a href="/" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Back to Chronos</a>
        </p>
      </div>

    </div>
  </div>
);

export default TermsOfService;
